const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MAX_DELAY = 300;
const DOUBLE_TAP_MAX_DISTANCE = 30;
const TAP_MAX_MOVEMENT = 10;
const ZOOM_RESET_THRESHOLD = 1.01;

class LightboxSlideZoom {
  constructor(slideEl) {
    this.slideEl = slideEl;
    this.imageEl = slideEl.querySelector(".lightbox-slider-image");
    if (!this.imageEl) return;

    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.originX = 50;
    this.originY = 50;

    this.baseWidth = 0;
    this.baseHeight = 0;

    this.pinching = false;
    this.panning = false;
    this.startDistance = 0;
    this.startScale = 1;
    this.startPanX = 0;
    this.startPanY = 0;
    this.gestureStartX = 0;
    this.gestureStartY = 0;

    this.tapStartX = 0;
    this.tapStartY = 0;
    this.touchMoved = false;
    this.lastTapTime = 0;
    this.lastTapX = 0;
    this.lastTapY = 0;

    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    this.slideEl.addEventListener("touchstart", this.handleTouchStart, { passive: false });
    this.slideEl.addEventListener("touchmove", this.handleTouchMove, { passive: false });
    this.slideEl.addEventListener("touchend", this.handleTouchEnd, { passive: false });
    this.slideEl.addEventListener("touchcancel", this.handleTouchEnd, { passive: false });
  }

  getDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt((dx * dx) + (dy * dy));
  }

  getMidpoint(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  ensureBaseSize() {
    if (this.scale > 1 && this.baseWidth && this.baseHeight) return;
    const rect = this.imageEl.getBoundingClientRect();
    if (rect.width && rect.height) {
      this.baseWidth = rect.width;
      this.baseHeight = rect.height;
    }
  }

  setOriginFromPoint(clientX, clientY) {
    const rect = this.imageEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.originX = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    this.originY = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
  }

  setTransition(enabled) {
    this.imageEl.style.transition = enabled ? "transform 0.25s ease-out" : "none";
  }

  applyTransform() {
    this.imageEl.style.transformOrigin = `${this.originX}% ${this.originY}%`;
    this.imageEl.style.transform = `scale(${this.scale}) translate(${this.panX}px, ${this.panY}px)`;
    this.slideEl.classList.toggle("is-touch-zoomed", this.scale > ZOOM_RESET_THRESHOLD);
  }

  clampPan() {
    if (this.scale <= 1 || !this.baseWidth || !this.baseHeight) {
      this.panX = 0;
      this.panY = 0;
      return;
    }
    const maxPanX = (this.baseWidth * (this.scale - 1)) / (2 * this.scale);
    const maxPanY = (this.baseHeight * (this.scale - 1)) / (2 * this.scale);
    this.panX = Math.min(maxPanX, Math.max(-maxPanX, this.panX));
    this.panY = Math.min(maxPanY, Math.max(-maxPanY, this.panY));
  }

  handleTouchStart(event) {
    if (event.touches.length === 2) {
      event.stopPropagation();
      event.preventDefault();
      this.ensureBaseSize();
      this.pinching = true;
      this.panning = false;
      this.setTransition(false);
      this.startDistance = this.getDistance(event.touches);
      this.startScale = this.scale;
      this.startPanX = this.panX;
      this.startPanY = this.panY;
      const midpoint = this.getMidpoint(event.touches);
      this.setOriginFromPoint(midpoint.x, midpoint.y);
      return;
    }

    if (event.touches.length === 1) {
      this.tapStartX = event.touches[0].clientX;
      this.tapStartY = event.touches[0].clientY;
      this.touchMoved = false;

      if (this.scale > ZOOM_RESET_THRESHOLD) {
        event.stopPropagation();
        this.panning = true;
        this.setTransition(false);
        this.gestureStartX = event.touches[0].clientX;
        this.gestureStartY = event.touches[0].clientY;
        this.startPanX = this.panX;
        this.startPanY = this.panY;
      }
    }
  }

  handleTouchMove(event) {
    if (this.pinching && event.touches.length === 2) {
      event.stopPropagation();
      event.preventDefault();
      const distance = this.getDistance(event.touches);
      if (!this.startDistance) return;
      const ratio = distance / this.startDistance;
      this.scale = Math.min(MAX_SCALE, Math.max(1, this.startScale * ratio));
      this.clampPan();
      this.applyTransform();
      return;
    }

    if (this.panning && event.touches.length === 1) {
      event.stopPropagation();
      event.preventDefault();
      const deltaX = (event.touches[0].clientX - this.gestureStartX) / this.scale;
      const deltaY = (event.touches[0].clientY - this.gestureStartY) / this.scale;
      this.panX = this.startPanX + deltaX;
      this.panY = this.startPanY + deltaY;
      this.clampPan();
      this.applyTransform();
      return;
    }

    if (event.touches.length === 1 && !this.pinching && !this.panning) {
      const deltaX = event.touches[0].clientX - this.tapStartX;
      const deltaY = event.touches[0].clientY - this.tapStartY;
      if (Math.abs(deltaX) > TAP_MAX_MOVEMENT || Math.abs(deltaY) > TAP_MAX_MOVEMENT) {
        this.touchMoved = true;
      }
    }
  }

  handleTouchEnd(event) {
    if (event.touches.length >= 2) return;

    if (event.touches.length === 1) {
      if (this.pinching) {
        this.pinching = false;
        if (this.scale > ZOOM_RESET_THRESHOLD) {
          this.panning = true;
          this.gestureStartX = event.touches[0].clientX;
          this.gestureStartY = event.touches[0].clientY;
          this.startPanX = this.panX;
          this.startPanY = this.panY;
        }
      }
      return;
    }

    const wasGesture = this.pinching || this.panning;
    this.pinching = false;
    this.panning = false;

    if (this.scale <= ZOOM_RESET_THRESHOLD) {
      this.reset();
    } else {
      this.setTransition(false);
    }

    if (!wasGesture && !this.touchMoved) {
      this.handleTap(event);
    }
  }

  handleTap(event) {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const now = Date.now();
    const dx = touch.clientX - this.lastTapX;
    const dy = touch.clientY - this.lastTapY;
    const isDoubleTap =
      now - this.lastTapTime < DOUBLE_TAP_MAX_DELAY &&
      Math.sqrt((dx * dx) + (dy * dy)) < DOUBLE_TAP_MAX_DISTANCE;

    this.lastTapTime = now;
    this.lastTapX = touch.clientX;
    this.lastTapY = touch.clientY;

    if (!isDoubleTap) return;

    event.preventDefault();
    this.lastTapTime = 0;
    this.setTransition(true);

    if (this.scale > ZOOM_RESET_THRESHOLD) {
      this.reset();
      return;
    }

    this.ensureBaseSize();
    this.setOriginFromPoint(touch.clientX, touch.clientY);
    this.scale = DOUBLE_TAP_SCALE;
    this.panX = 0;
    this.panY = 0;
    this.applyTransform();
  }

  reset() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.originX = 50;
    this.originY = 50;
    this.pinching = false;
    this.panning = false;
    this.setTransition(true);
    this.applyTransform();
  }
}

class LightboxZoomController {
  constructor(root = document) {
    this.instances = new WeakMap();
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResize, { passive: true });
    this.mount(root);
  }

  mount(root = document) {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    Array.from(scope.querySelectorAll(".lightbox-slider[data-blaze-slider]")).forEach((slider) =>
      this.bindSlider(slider),
    );
  }

  bindSlider(slider) {
    if (!slider || slider.dataset.zoomTouchBound === "true") return;
    slider.dataset.zoomTouchBound = "true";

    const zoomInstances = Array.from(slider.querySelectorAll(".lightbox-slider-slide")).map(
      (slide) => new LightboxSlideZoom(slide),
    );
    const resetAll = () => zoomInstances.forEach((instance) => instance.reset());

    slider.addEventListener("slidechange", resetAll);

    const modal = slider.closest("[data-lightbox-modal]");
    const modalId = modal?.dataset?.modal;
    if (modalId) {
      window.addEventListener(`${modalId}-modal-closed`, resetAll);
    }

    this.instances.set(slider, { zoomInstances, resetAll });
  }

  handleResize() {
    if (typeof document === "undefined") return;
    document.querySelectorAll(".lightbox-slider[data-blaze-slider]").forEach((slider) => {
      this.instances.get(slider)?.resetAll();
    });
  }
}

const bootstrap = (root = document) => {
  if (!root) return;
  if (!window.__quickfireLightboxZoom) {
    window.__quickfireLightboxZoom = new LightboxZoomController(root);
  } else {
    window.__quickfireLightboxZoom.mount(root);
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => bootstrap());
} else {
  bootstrap();
}

document.addEventListener("shopify:section:load", (event) => {
  bootstrap(event?.target || document);
});

export { LightboxZoomController };
